/**
 * Shared Home backgrounds are uploaded through the backend.
 * The client does not choose a Storage path and does not write the bucket.
 *
 * Personal launcher wallpapers remain a browser store. The owner-only
 * Storage path below is unused by Settings and is covered by Storage rules.
 */
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { storage } from '../firebase';
import { readDealerAdminSession } from '../../pages/Admin/dealerAdminSession';
import {
  readSupportedTenantSession,
  requestContextHeaders,
  supportHeadersForEndpoint,
} from '../../pages/Admin/supportedTenantsSession';
import { backendApiUrl } from '../../config/backendApiUrl';

const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);
const MAX_SIZE_MB = 10;
const STORAGE_ID = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;
const ASSET_ID = /^[a-f0-9]{32}$/;
export const MANAGED_BACKGROUND_PREFIX = 'sb-background://';

const assertStorageId = (value, label) => {
  const text = String(value || '').trim();
  if (!STORAGE_ID.test(text)) throw new Error(`${label} is not valid`);
  return text;
};

const assertUploadableImage = (file) => {
  if (!file) throw new Error('file is required');
  if (!ALLOWED_TYPES.has(file.type)) {
    throw new Error(`Unsupported file type: ${file.type}. Use JPEG, PNG, WebP, or GIF.`);
  }
  if (file.size > MAX_SIZE_MB * 1024 * 1024) {
    throw new Error(`File too large (max ${MAX_SIZE_MB} MB).`);
  }
};

export const personalBackgroundObjectPath = (uid, filename) => {
  const owner = assertStorageId(uid, 'uid');
  const name = assertStorageId(String(filename || '').replace(/\./g, '_'), 'filename');
  return `users/${owner}/launcher-background/${name}`;
};

export const managedBackgroundReference = (workspaceId, assetId) => {
  const workspace = assertStorageId(workspaceId, 'workspaceId');
  const asset = String(assetId || '').trim().toLowerCase();
  if (!ASSET_ID.test(asset)) throw new Error('assetId is not valid');
  return `${MANAGED_BACKGROUND_PREFIX}${workspace}/${asset}`;
};

export const parseManagedBackground = (value) => {
  const text = String(value || '');
  if (!text.startsWith(MANAGED_BACKGROUND_PREFIX)) return null;
  const [workspaceId, assetId] = text.slice(MANAGED_BACKGROUND_PREFIX.length).split('/');
  if (!STORAGE_ID.test(workspaceId || '') || !ASSET_ID.test(assetId || '')) return null;
  return { workspaceId, assetId };
};

export const resolveStudioBackgroundTenantId = (authTenantId) => {
  const dealerTenantId = readDealerAdminSession()?.tenantId;
  if (dealerTenantId) return dealerTenantId;
  const supportTenantId = readSupportedTenantSession()?.tenantId;
  if (supportTenantId) return supportTenantId;
  return String(authTenantId || '').trim();
};

export const studioBackgroundWritesLocked = () => (
  !readDealerAdminSession() && readSupportedTenantSession()?.accessLevel === 'view'
);

const authHeaders = async (tenantId) => {
  const token = await getAuth().currentUser?.getIdToken();
  if (!token) throw new Error('User not authenticated');
  const endpoint = '/api/v2/workspaces/background';
  return {
    Authorization: `Bearer ${token}`,
    ...requestContextHeaders(),
    ...supportHeadersForEndpoint(endpoint),
    ...(tenantId ? {} : {}),
  };
};

const backgroundUrl = (workspaceId, tenantId, { assetId = '', content = false } = {}) => {
  const workspace = assertStorageId(workspaceId, 'workspaceId');
  const params = new URLSearchParams();
  if (assetId) params.set('assetId', assetId);
  const sessionTenant = readDealerAdminSession()?.tenantId || readSupportedTenantSession()?.tenantId;
  if (!sessionTenant && tenantId) params.set('tenantId', tenantId);
  const query = params.toString();
  const suffix = content ? '/content' : '';
  return backendApiUrl(`/api/v2/workspaces/${encodeURIComponent(workspace)}/background${suffix}${query ? `?${query}` : ''}`);
};

export const uploadWorkspaceBackground = async (tenantId, workspaceId, file) => {
  assertUploadableImage(file);
  assertStorageId(workspaceId, 'workspaceId');
  const headers = await authHeaders(tenantId);
  const response = await fetch(backgroundUrl(workspaceId, tenantId), {
    method: 'POST',
    headers: { ...headers, 'Content-Type': file.type },
    body: file,
  });
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`API Error (${response.status}): ${text}`);
  }
  const payload = JSON.parse(text);
  const data = payload?.data || {};
  if (!data.reference || !parseManagedBackground(data.reference)) {
    throw new Error('The background was not accepted');
  }
  return data.reference;
};

export const loadWorkspaceBackground = async (workspaceId, assetId, tenantId) => {
  const headers = await authHeaders(tenantId);
  const response = await fetch(backgroundUrl(workspaceId, tenantId, { assetId, content: true }), {
    method: 'GET',
    headers,
  });
  if (!response.ok) {
    throw new Error(`API Error (${response.status})`);
  }
  return response.blob();
};

export const deleteManagedBackground = async (workspaceId, assetId, tenantId) => {
  const headers = await authHeaders(tenantId);
  const response = await fetch(backgroundUrl(workspaceId, tenantId, { assetId }), {
    method: 'DELETE',
    headers,
  });
  if (!response.ok) {
    throw new Error(`API Error (${response.status})`);
  }
};

export const backgroundFailureMessage = (error) => {
  const text = String(error?.message || '');
  if (text.includes('(401)') || text.includes('(403)') || text.includes('storage/unauthorized')) {
    return 'Home Designer needs a refresh before it can change this background. Reload Signal Bridge and try again.';
  }
  if (text.includes('layout_conflict') || text.includes('(409)')) {
    return 'This Home draft was updated somewhere else. The background on this page was not changed.';
  }
  return 'The background was not changed. Try again.';
};

/**
 * The client shipped before this release. It has no refresh sentence.
 * A failed Storage upload leaves the selected image in place.
 */
export const legacyCachedClientUploadResult = (previous, error) => ({
  backgroundImageUrl: previous || '',
  message: error?.message || 'Upload failed',
  saved: false,
});

export const commitBackgroundChange = async ({ previous, upload, save }) => {
  let created = previous || '';
  try {
    created = await upload();
  } catch (error) {
    return {
      backgroundImageUrl: previous || '',
      message: backgroundFailureMessage(error),
      saved: false,
    };
  }
  try {
    await save(created);
  } catch (error) {
    return {
      backgroundImageUrl: previous || '',
      message: backgroundFailureMessage(error),
      saved: false,
    };
  }
  return { backgroundImageUrl: created, message: '', saved: true };
};

export const uploadLauncherBackground = async (uid, file) => {
  assertUploadableImage(file);
  const ext = String(file.name || '').split('.').pop().toLowerCase() || 'jpg';
  const safeExt = ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext) ? ext : 'jpg';
  const path = personalBackgroundObjectPath(uid, `bg-${Date.now()}-${safeExt}`);
  const storageRef = ref(storage, path);
  const snapshot = await uploadBytes(storageRef, file, { contentType: file.type });
  return getDownloadURL(snapshot.ref);
};

export const isUserLauncherBackground = (downloadUrl, uid) => {
  if (!downloadUrl || !uid) return false;
  try {
    return decodeURIComponent(String(downloadUrl)).includes(`users/${uid}/launcher-background`);
  } catch {
    return String(downloadUrl).includes(`users/${uid}/launcher-background`);
  }
};

export const removeWorkspaceBackground = async (downloadUrl) => {
  const managed = parseManagedBackground(downloadUrl);
  if (managed) {
    try {
      await deleteManagedBackground(managed.workspaceId, managed.assetId);
    } catch (err) {
      console.warn('[WorkspaceBackgroundService] Failed to delete background (non-fatal):', err?.message);
    }
    return;
  }
  if (!downloadUrl || String(downloadUrl).startsWith('http')) return;
  try {
    await deleteObject(ref(storage, downloadUrl));
  } catch (err) {
    console.warn('[WorkspaceBackgroundService] Failed to delete old background (non-fatal):', err?.message);
  }
};
